import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { orderAPI, reviewAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import './Dashboard.css';

const STATUS_COLORS = { PENDING:'warning', CONFIRMED:'info', IN_PROGRESS:'info', COMPLETED:'success', CANCELLED:'error', REFUNDED:'error' };

export default function BuyerDashboard() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewModal, setReviewModal] = useState(null);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: '' });

  useEffect(() => {
    orderAPI.getAll({ role: 'buyer', limit: 50 })
      .then(r => setOrders(r.data.orders))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this order?')) return;
    try {
      await orderAPI.updateStatus(id, 'CANCELLED');
      setOrders(o => o.map(ord => ord.id === id ? { ...ord, status: 'CANCELLED' } : ord));
      toast.success('Order cancelled');
    } catch { toast.error('Failed to cancel order'); }
  };

  const handleReview = async (e) => {
    e.preventDefault();
    try {
      await reviewAPI.create({ orderId: reviewModal.id, ...reviewForm });
      toast.success('Review submitted!');
      setOrders(o => o.map(ord => ord.id === reviewModal.id ? { ...ord, review: reviewForm } : ord));
      setReviewModal(null);
    } catch (err) {
      toast.error(err.response?.data?.error?.message || 'Failed to submit review');
    }
  };

  if (loading) return <div className="loading-page"><div className="spinner" /></div>;

  const active = orders.filter(o => ['PENDING','CONFIRMED','IN_PROGRESS'].includes(o.status));
  const completed = orders.filter(o => o.status === 'COMPLETED');
  const cancelled = orders.filter(o => ['CANCELLED','REFUNDED'].includes(o.status));

  return (
    <div className="dashboard-page page">
      <div className="container">
        <div className="dashboard-header">
          <div>
            <h1>My Orders</h1>
            <p className="text-light">Welcome, {user.firstName}! Manage your orders here.</p>
          </div>
          <Link to="/browse" className="btn btn-primary">Browse Services</Link>
        </div>

        <div className="stats-grid">
          {[
            { label: 'Total Orders', value: orders.length, icon: '🛒' },
            { label: 'Active', value: active.length, icon: '⏳' },
            { label: 'Completed', value: completed.length, icon: '✅' },
            { label: 'Cancelled', value: cancelled.length, icon: '❌' },
          ].map(s => (
            <div key={s.label} className="stat-card">
              <div className="stat-icon">{s.icon}</div>
              <div className="stat-value">{s.value}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          ))}
        </div>

        {orders.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🛒</div>
            <h3>No orders yet</h3>
            <p>Start exploring services from African professionals in your community.</p>
            <Link to="/browse" className="btn btn-primary">Browse Services</Link>
          </div>
        ) : (
          <div className="orders-list">
            {orders.map(o => {
              const fallback = `https://ui-avatars.com/api/?name=${encodeURIComponent(o.service?.title || 'S')}&size=60&background=FDE68A&color=92400E&format=svg`;
              return (
                <div key={o.id} className="order-card">
                  <img
                    className="order-card-img"
                    src={o.service?.images?.[0] ? `http://localhost:5000${o.service.images[0]}` : fallback}
                    alt={o.service?.title}
                    onError={e => { e.target.src = fallback; }}
                  />
                  <div className="order-card-info">
                    <h4><Link to={`/orders/${o.id}`} className="text-primary">{o.service?.title}</Link></h4>
                    <div className="order-card-meta">
                      <span>Seller: {o.seller?.businessName || `${o.seller?.firstName} ${o.seller?.lastName}`}</span>
                      <span> · </span>
                      <span>{new Date(o.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="order-card-right">
                    <span className="font-bold text-primary">${o.totalAmount?.toFixed(2)}</span>
                    <span className={`badge badge-${STATUS_COLORS[o.status]}`}>{o.status}</span>
                    <div className="flex gap-1">
                      {['PENDING'].includes(o.status) && (
                        <button className="btn btn-danger btn-sm" onClick={() => handleCancel(o.id)}>Cancel</button>
                      )}
                      {o.status === 'COMPLETED' && !o.review && (
                        <button className="btn btn-primary btn-sm" onClick={() => setReviewModal(o)}>Leave Review</button>
                      )}
                      {o.status === 'COMPLETED' && o.review && (
                        <span className="badge badge-success">✅ Reviewed</span>
                      )}
                      <Link to={`/orders/${o.id}`} className="btn btn-ghost btn-sm">View</Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Review Modal */}
      {reviewModal && (
        <div className="modal-overlay" onClick={() => setReviewModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>⭐ Leave a Review</h2>
            <p className="text-light mb-2">For: <strong>{reviewModal.service?.title}</strong></p>
            <form onSubmit={handleReview}>
              <div className="form-group">
                <label className="form-label">Rating</label>
                <div className="star-picker">
                  {[1,2,3,4,5].map(n => (
                    <button key={n} type="button" className={`star-pick ${n <= reviewForm.rating ? 'filled' : ''}`}
                      onClick={() => setReviewForm(f => ({ ...f, rating: n }))}>★</button>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Comment (optional)</label>
                <textarea className="form-textarea" placeholder="Share your experience..."
                  value={reviewForm.comment} onChange={e => setReviewForm(f => ({ ...f, comment: e.target.value }))} rows={4} />
              </div>
              <div className="flex gap-1">
                <button type="button" className="btn btn-ghost" onClick={() => setReviewModal(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Submit Review</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
